import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import EditIcon from '@material-ui/icons/Edit';
import { toast } from 'react-toastify';

import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import LinearProgress from '~/components/LinearProgress';
import CircularProgress from '@material-ui/core/CircularProgress';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import isBlank from '~/utils/isBlank';
import Storage from '~/providers/Storage';
import Can from '~/contexts/Can';

import Paper from '~/components/Paper';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Request from '~/providers/Request';
import Button from '~/components/Button';

import MedicalRecordRecord from '~/records/MedicalRecordRecord';

import New from './New';
import Show from './Show';
import Edit from './Edit';
import MedicalRecordEvolution from '../MedicalRecordEvolution';

import { USERS_DOWNLOAD_ATTACHMENT } from '~/config/constants';


const useStyles = makeStyles(() => ({
  circularProgress: {
    color: 'grey',
    marginRight: '10px',
  },
}));

function MedicalRecord({ idClient, name}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [record, setRecord] = useState(new MedicalRecordRecord());
  const [loading, setLoading] = useState(false);

  const [reload, setReload] = useState(false);

  const [showButtonNew, setShowButtonNew] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function verifyPermissionsDownload() {
    const userIdsDownload = USERS_DOWNLOAD_ATTACHMENT;
    const userLoggedObj = await Storage.getItem(Storage.userKey);
    if (isBlank(userIdsDownload) || isBlank(userLoggedObj)) return;
    const arrIds = [];
    userIdsDownload
      .split(',')
      .forEach((user_id) => arrIds.push(parseInt(user_id.trim())));
    if (arrIds.indexOf(parseInt(userLoggedObj.id)) !== -1)
      setCanDownload(true);
  }

  useEffect(() => {
    async function getMedicalRecord() {
      setShowButtonNew(false);
      setLoading(true);
      Request.get(`/admin/medical_records/${idClient}/show`, {})
        .then((response) => {
          setLoading(false);
          setShowButtonNew(true);

          if (response.data) {
            setShowButtonNew(false);
            setRecord(record.updateAttributes(response.data));
            setIsShow(true);
          }
        })
        .catch((e) => {
          console.error(e);
          setShowButtonNew(false);
          setLoading(false);
        });
    }
    if (idClient) {
      getMedicalRecord();
    }
    verifyPermissionsDownload();
  }, [idClient, reload]);

  const handleAppointmentFile = async () => {
    setDownloading(true);
    toast.info('Arquivo esta sendo gerado!');
    Request.download(`/admin/attachments/${idClient}`, {responseType: 'arraybuffer'})
    .then((res) => res.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name ;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Arquivo gerado com sucesso!');
    })
    .catch((e) => {
      toast.error(
        'Não foi possível gerar o arquivo'
      );
      console.error(e);
    }).finally(() => {
      setDownloading(false);
    });
  }

  return (
    <>
      <LinearProgress loading={loading} />
      <Paper
        Icon={LocalHospitalIcon}
        iconColor="blue"
        title={t('person.tabs.medical_record')}
        loading={loading}
        actions={
          <div>
            {isShow && (
              <Can I="edit" a="MedicalRecords">
                <Button
                  color="orange"
                  onClick={() => {
                    setIsShow(false);
                    setIsEdit(true);
                  }}
                  endIcon={<EditIcon />}
                >
                  {t('action.edit')}
                </Button>
              </Can>
            )}
            {isShow && canDownload && (
                <Button
                  style={{ marginLeft: 20 }}
                  disabled={downloading}
                  onClick={() => handleAppointmentFile()}
                >
                  {downloading ? (
                    <>
                      <CircularProgress
                        size={20}
                        className={classes.circularProgress}
                      />
                      {t('action.downloading')}
                    </>
                  ) : (
                    <>
                      <SaveAltIcon style={{ marginRight: '10px' }} />
                      {t('action.download')}
                    </>
                  )}
                </Button>
            )}
          </div>
        }
      >
        {!isEdit && !isNew && !isShow && showButtonNew && (
          <>
            <Typography variant="subtitle1" align="center">
              Este paciente não possui prontuário médico
            </Typography>

            <Can I="new" a="MedicalRecords">
              <Typography color="primary" align="center">
                <Button color="primary" onClick={() => setIsNew(!isNew)}>
                  + Criar prontuário
                </Button>
              </Typography>
            </Can>
          </>
        )}
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Collapse in={isNew}>
              <New
                idClient={idClient}
                onCancel={() => setIsNew(!isNew)}
                onSuccess={() => {
                  setReload(!reload);
                  setIsNew(false);
                }}
              />
            </Collapse>
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Collapse in={isShow}>
              <Show idClient={idClient} record={record} />
            </Collapse>
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Collapse in={isEdit}>
              <Edit
                idClient={idClient}
                onCancel={() => {
                  setIsEdit(!isEdit);
                  setReload(!reload);
                }}
                record={record}
                setRecord={setRecord}
                onSuccess={() => {
                  setReload(!reload);
                  setIsEdit(false);
                }}
              />
            </Collapse>
          </GridItem>
        </GridContainer>
      </Paper>
      <LinearProgress loading={loading} />

      <Can I="list" a="MedicalRecordEvolutions">
        <MedicalRecordEvolution idClient={idClient} record={record} />
      </Can>
    </>
  );
}

export default React.memo(MedicalRecord);
